import { createFileRoute } from '@tanstack/react-router';
import { useCompany } from '@/lib/company-context';
import { usePlayerProfile, usePlayerRank } from '@/lib/queries/player';
import { useMyPrograms } from '@/lib/queries/programs';
import { useCompanyLeaderboard, useCompanyDashboard } from '@/lib/queries/corporate';
import { StatCard } from '@/components/shared/stat-card';
import { StatCardSkeleton } from '@/components/shared/skeleton';
import { ProgressBar } from '@/components/shared/progress-bar';
import { formatNumber } from '@/lib/format';
import { Trophy, Flame, Route as RouteIcon, TrendingUp, Users, Leaf } from 'lucide-react';
import { Link } from '@tanstack/react-router';

export const Route = createFileRoute('/app/')({
  component: HomePage,
});

function HomePage() {
  const { company, level } = useCompany();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-foreground">Welcome back</h1>
        <p className="text-sm text-muted-foreground">{company.name}</p>
      </div>

      <PersonalKPIs />
      <ActivePrograms />
      <LeaderboardPreview />
      {level >= 5 && <CompanyPulse />}
    </div>
  );
}

function PersonalKPIs() {
  const { data: profile, isLoading: profileLoading } = usePlayerProfile();
  const { data: rank, isLoading: rankLoading } = usePlayerRank();

  if (profileLoading || rankLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>
    );
  }

  const weeklyRank = rank?.ranks?.['weekly'];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <StatCard
        icon={Trophy}
        label="Your Rank"
        value={weeklyRank?.rank ? `#${weeklyRank.rank}` : '--'}
        subtitle="this week"
      />
      <StatCard
        icon={Flame}
        label="Points"
        value={profile?.lifetime_points ? formatNumber(profile.lifetime_points) : '--'}
        subtitle="lifetime"
      />
      <StatCard
        icon={RouteIcon}
        label="Trips"
        value={profile?.trips_completed ? formatNumber(profile.trips_completed) : '--'}
        subtitle="lifetime"
      />
    </div>
  );
}

function ActivePrograms() {
  const { data, isLoading } = useMyPrograms();

  if (isLoading) return null;

  const programs = data?.programs;
  if (!programs?.length) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">Active Programs</h2>
      {programs
        .slice(0, 3)
        .map(
          (p: {
            program_id: string;
            program_name: string;
            progress_percent?: number;
            benefits_count?: number;
          }) => (
            <Link
              key={p.program_id}
              to="/app/programs/$programId"
              params={{ programId: p.program_id }}
              className="block rounded-xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-foreground">{p.program_name}</p>
                {p.progress_percent != null && (
                  <span className="text-xs font-medium text-muted-foreground tabular-nums">
                    {Math.round(p.progress_percent)}%
                  </span>
                )}
              </div>
              {p.progress_percent != null && <ProgressBar value={p.progress_percent} />}
              {p.benefits_count != null && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {p.benefits_count} benefit{p.benefits_count !== 1 ? 's' : ''} available
                </p>
              )}
            </Link>
          )
        )}
    </div>
  );
}

function LeaderboardPreview() {
  const { company } = useCompany();
  const { data, isLoading } = useCompanyLeaderboard(company.slug, 'weekly', 5);

  if (isLoading) return null;
  if (!data?.entries?.length) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-foreground">Leaderboard</h2>
        <Link to="/app/leaderboard" className="text-xs font-medium text-primary hover:underline">
          View all &rarr;
        </Link>
      </div>

      {data.my_rank && (
        <div className="mb-3 rounded-lg bg-primary/10 p-3 flex items-center gap-3">
          <span className="text-sm font-bold text-primary tabular-nums">
            #{data.my_rank.rank || '--'}
          </span>
          <span className="text-sm font-medium text-foreground">{data.my_rank.display_name}</span>
          <span className="ml-auto text-sm font-medium text-muted-foreground tabular-nums">
            {formatNumber(data.my_rank.points)} pts
          </span>
        </div>
      )}

      <div className="space-y-2">
        {data.entries.map(
          (entry: { user_id: string; rank: number; display_name: string; points: number }) => (
            <div key={entry.user_id} className="flex items-center gap-3 py-1">
              <span className="w-6 text-right text-sm font-medium text-muted-foreground tabular-nums">
                {entry.rank >= 1 && entry.rank <= 3
                  ? ['\u{1F947}', '\u{1F948}', '\u{1F949}'][entry.rank - 1]
                  : `#${entry.rank}`}
              </span>
              <span className="flex-1 text-sm text-foreground truncate">{entry.display_name}</span>
              <span className="text-sm text-muted-foreground tabular-nums">
                {formatNumber(entry.points)}
              </span>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function CompanyPulse() {
  const { company } = useCompany();
  const { data, isLoading } = useCompanyDashboard(company.slug, 7);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-foreground">Company Pulse</h2>
        <Link to="/app/dashboard" className="text-xs font-medium text-primary hover:underline">
          Full dashboard &rarr;
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Active Members"
          value={data.active_members}
          subtitle="last 7 days"
        />
        <StatCard
          icon={RouteIcon}
          label="Trips"
          value={formatNumber(data.stats.total_trips)}
          subtitle="total"
        />
        <StatCard
          icon={TrendingUp}
          label="Points"
          value={formatNumber(data.stats.total_points)}
          subtitle="total"
        />
        <StatCard
          icon={Leaf}
          label="CO\u2082 Avoided"
          value={formatNumber(Math.round(data.carbon.avoided_emissions_g / 1000))}
          subtitle="kg"
        />
      </div>
    </div>
  );
}
